import {
  delay,
  put,
  race,
  take,
  select,
  call,
  fork,
  cancel,
  cancelled,
} from 'redux-saga/effects';
import { Task } from 'redux-saga';
import { fetchMeRequest } from '../actions';
import { getAuthUser } from '../selectors';
import { LOGOUT_REQUEST, LOGIN_SUCCESS, FETCH_ME_SUCCESS } from '../constants';

// Session check interval: 5 minutes in milliseconds
const SESSION_CHECK_INTERVAL = 2 * 60 * 1000;

/**
 * Pattern to match any successful API action.
 * All SUCCESS actions in the app end with '-success' suffix.
 */
function isSuccessAction(action: { type: string }): boolean {
  return action.type.endsWith('-success');
}

/**
 * The main session timer loop.
 * Races between:
 * 1. A 5-minute delay (session check timeout)
 * 2. Any successful API action (which resets the timer)
 * 3. Logout request (which stops the timer)
 */
function* sessionTimerLoop() {
  try {
    while (true) {
      const { timeout, logout } = yield race({
        timeout: delay(SESSION_CHECK_INTERVAL),
        success: take(isSuccessAction),
        logout: take(LOGOUT_REQUEST),
      });

      if (logout) {
        // User logged out, stop the session check
        return;
      }

      if (timeout) {
        // Timer expired without any API activity - session check ping disabled temporarily.
        // The code below is kept for potential future reuse.
        //
        // const user = yield select(getAuthUser);
        // if (user) {
        //   // Pass true for isSessionCheck to skip unnecessary processing in fetchMeSaga
        //   yield put(fetchMeRequest(true));
        // }
      }

      // If success action was received, the loop continues and timer resets
    }
  } finally {
    if (yield cancelled()) {
      // Cleanup if needed when saga is cancelled
    }
  }
}

/**
 * Watches for login success and starts the session timer.
 * Also handles restarting the timer if user is already logged in on app load.
 */
let sessionTask: Task | null = null;

export function* watchSessionCheck() {
  while (true) {
    // Wait for either a successful login or successful fetchMe (app load with existing session)
    yield take([LOGIN_SUCCESS, FETCH_ME_SUCCESS]);

    // Cancel any existing session timer
    if (sessionTask) {
      yield cancel(sessionTask);
    }

    // Start the session timer
    sessionTask = yield fork(sessionTimerLoop);
  }
}

/**
 * Main session check saga entry point.
 * Call this from the root auth saga.
 */
export function* sessionCheckSaga() {
  yield fork(watchSessionCheck);
}
